import React from 'react';
import { AlertTriangle, ShieldAlert, Pencil, Zap, Check, Wand2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

/**
 * ConflictModal — Pre-generate conflict surfacing (Feb 26, 2026)
 *
 * Shown when `scanNotesForConflicts(notes)` returns one or more conflicts.
 *
 * NEW (v6.7.7) — Interactive fix actions. Each conflict can carry a
 * `fixActions: [{ id, label, kind, payload }]` array. Tapping a fix button
 * calls `onApplyFix(conflictId, action)` which the parent uses to rewrite
 * the notes input AND clear the conflict from the modal.
 *
 *   - Pronoun mix → "Patient is Male" / "Patient is Female"
 *   - Endorse/denies → "Keep Endorses <sym>" / "Keep Denies <sym>"
 *
 * After all conflicts are resolved, the modal auto-closes and Generate
 * proceeds. If the user leaves any high-severity conflict unresolved, the
 * "Generate Anyway" button still works as escape hatch.
 */

const severityStyles = {
  high: {
    Icon: ShieldAlert,
    iconCls: 'text-rose-400',
    cardBg: 'bg-rose-500/10 border-rose-500/40',
    tag: 'High — Clinical Risk',
    tagCls: 'text-rose-300 bg-rose-500/20',
  },
  medium: {
    Icon: AlertTriangle,
    iconCls: 'text-amber-400',
    cardBg: 'bg-amber-500/10 border-amber-500/40',
    tag: 'Medium — Formatting',
    tagCls: 'text-amber-300 bg-amber-500/20',
  },
};

const ConflictModal = ({
  open,
  conflicts = [],
  onEditNotes,
  onGenerateAnyway,
  onApplyFix,    // (conflictId, action) — parent rewrites notes + clears that conflict
}) => {
  const hasHigh = conflicts.some((c) => c.severity === 'high');

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onEditNotes && onEditNotes(); }}
    >
      <DialogContent
        className="sm:max-w-2xl bg-card border-border max-h-[85vh] overflow-y-auto"
        data-testid="conflict-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${hasHigh ? 'text-rose-400' : 'text-amber-400'}`} />
            {hasHigh ? 'Possible Conflicts in Your Notes' : 'Heads-up Before Generate'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {hasHigh
              ? 'Found something that often causes the AI to drop or contradict clinical content. Review below before generating.'
              : 'Found a formatting issue that may affect output quality. You can edit or proceed anyway.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {conflicts.map((c) => {
            const s = severityStyles[c.severity] || severityStyles.medium;
            const Icon = s.Icon;
            return (
              <div
                key={c.id}
                className={`p-3 rounded-lg border ${s.cardBg}`}
                data-testid={`conflict-item-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${s.iconCls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.tagCls}`}>
                        {s.tag}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground leading-snug">
                      {c.title}
                    </h3>
                    <pre className="text-xs text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap font-sans">
                      {c.detail}
                    </pre>
                    {c.suggestion && (
                      <p className="text-[11px] text-foreground/80 mt-2 italic">
                        {c.suggestion}
                      </p>
                    )}
                    {/* Interactive fix buttons (Feb 26, 2026) */}
                    {Array.isArray(c.fixActions) && c.fixActions.length > 0 && onApplyFix && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`conflict-fixes-${c.id}`}>
                        {c.fixActions.map((action) => (
                          <Button
                            key={action.id}
                            size="sm"
                            variant="outline"
                            onClick={() => onApplyFix(c.id, action)}
                            className="h-7 px-2.5 text-[11px] gap-1 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200 hover:border-emerald-500/60"
                            data-testid={`conflict-fix-${action.id}`}
                            title={`Apply: ${action.label}`}
                          >
                            <Wand2 className="w-3 h-3" />
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={onGenerateAnyway}
            className="border-border gap-1"
            data-testid="conflict-generate-anyway-btn"
            title="I've reviewed — proceed with the current notes"
          >
            <Zap className="w-4 h-4" />
            Generate Anyway
          </Button>
          <Button
            onClick={onEditNotes}
            className="bg-amber-500/90 hover:bg-amber-500 text-amber-950 font-semibold gap-1"
            data-testid="conflict-edit-btn"
            title="Close the dialog and edit the notes"
          >
            <Pencil className="w-4 h-4" />
            Edit Notes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictModal;
